/**
 * Phase 4 — Step 0 catalogue-field resolvers.
 *
 * Pure helpers that walk a `ResolvedBuilding.raw` payload and surface the
 * production / static / GB-level contribution it makes. Every dual-format
 * trap from `scripts/audit-catalogue-fields.ts` is encoded here in priority
 * order; the matrix in `docs/data-formats.md` is the spec.
 *
 * Knowledge owned by this module (NOT by the catalogue resolver in
 * `lib/catalogue/normalize.ts`, which intentionally stays minimal):
 *
 *   1. Standard buildings encode production at the top level under
 *      `available_products[]`. Each entry has a `production_time`
 *      (seconds) and either `product.resources.{money,supplies,goods,...}`
 *      or, for military, `unit_type_id` + `amount` + `time_to_train`.
 *
 *   2. Multi-age event buildings encode production under either
 *      `components.AllAge.production` (fixed-data events) or
 *      `components.{Era}.production` (era-keyed events; current-era block
 *      selected via the player's town hall era). Each block has
 *      `options[*].time` and `options[*].products[*].playerResources.resources`.
 *
 *   3. Capacity-style fields (population, happiness) live in
 *      `staticResources.resources.{population,happiness,...}` (top-level)
 *      or in `provided_happiness` (top-level, culture buildings) or in
 *      `components.{Era}.happiness.provided` (multi-age bonus residentials)
 *      or in `entity_levels[*].provided_happiness` (level-able event
 *      buildings — v1 reads level 0 as a simplification).
 *
 *   4. Great Buildings have `entity_levels: []` (empty) in the catalogue
 *      and use `production_bonus.values[level]` instead, with a
 *      heterogeneous `production_bonus.type`. Only known player-side types
 *      are surfaced (FP, happiness, medals, population). `contribution_boost`,
 *      `att_boost_attacker`, etc. are guild-affecting / multiplicative and
 *      excluded from v1 totals.
 *
 *   5. Random-production options (multiple options per production block)
 *      are read as the FIRST option only in v1. The selected option lives
 *      in `placement.state` and isn't surfaced into our types yet — track
 *      under deferreds.
 *
 * Resolver outputs are deliberately simple Maps so callers can sum
 * across buildings without walking nested structures.
 */

const SECONDS_PER_DAY = 86400

/** Resource key → per-day amount. e.g. { money: 1234, marble: 5 }. */
export type ResourceMap = Map<string, number>
/** Unit type id → per-day count. e.g. { rogue: 1, light_melee: 2 }. */
export type UnitMap = Map<string, number>

export interface CycleContribution {
  resources: ResourceMap
  units: UnitMap
}

export interface StaticContribution {
  /** Per-day output for resources that are flat capacity (population,
   *  happiness) AND for resources whose `staticResources` is genuinely a
   *  per-day flow (rare; some Watchfire-like buildings). v1 collapses both
   *  into one bucket — the cycle path doesn't double-count because it reads
   *  `available_products[]` and `components.{Era}.production`, neither of
   *  which overlap with `staticResources.resources`. */
  resources: ResourceMap
}

export interface GBLevelBonus {
  /** Resource key produced by the GB at the given level. Empty Map when the
   *  GB's `production_bonus.type` isn't one we surface in v1 (e.g.
   *  `contribution_boost`, `att_boost_attacker`). */
  resources: ResourceMap
}

function isObj(x: unknown): x is Record<string, unknown> {
  return typeof x === 'object' && x !== null && !Array.isArray(x)
}

function addResource(map: ResourceMap, key: string, value: number): void {
  // Allow negative values — multi-age residentials at high tiers can
  // consume population (e.g. -1070). The map sums; callers see the net.
  if (!Number.isFinite(value) || value === 0) return
  map.set(key, (map.get(key) ?? 0) + value)
}

/**
 * Read `components.{...}.staticResources` whose shape is:
 *   { type: 'staticResources', resources: { resources: { key: value } } }
 * The double-nesting is real and load-bearing — see the audit comment
 * in resolveStaticContribution.
 */
function readDoubleNestedResources(
  staticResources: unknown,
  into: ResourceMap,
): void {
  if (!isObj(staticResources)) return
  const outer = staticResources.resources
  if (!isObj(outer)) return
  const inner = outer.resources
  if (!isObj(inner)) return
  for (const [key, val] of Object.entries(inner)) {
    if (typeof val === 'number') addResource(into, key, val)
  }
}

/**
 * Pick the era block under `components` that has a non-empty
 * staticResources subtree. When `playerEra` is supplied, ONLY that era is
 * tried — no fallback to other eras. Reason: a building's static
 * contribution (population, happiness) at era X does not transfer to a
 * player in era Y. A Trailside-class event has BronzeAge population data
 * but at SpaceAgeSpaceHub the tier no longer provides population (only
 * happiness). Falling back to BronzeAge would inflate the player's
 * population total by ~10×.
 *
 * When `playerEra` is undefined (rare; degraded import), fall back to the
 * first non-AllAge era so we still surface SOMETHING — same defensive
 * fallback used by `pickProductionBlock`.
 */
function pickEraBlockWithStaticResources(
  components: Record<string, unknown>,
  playerEra: string | undefined,
): Record<string, unknown> | null {
  const tryBlock = (key: string): Record<string, unknown> | null => {
    const b = components[key]
    if (!isObj(b)) return null
    if (!isObj(b.staticResources)) return null
    return b
  }
  if (playerEra) {
    return tryBlock(playerEra)
  }
  for (const k of Object.keys(components)) {
    if (k === 'AllAge') continue
    const b = tryBlock(k)
    if (b) return b
  }
  return null
}

/**
 * Pick the components production block matching the player's era. Mirrors
 * `lib/catalogue/details.ts::pickProductionBlock` — duplicated here so the
 * stats module doesn't depend on the details module.
 */
function pickProductionBlock(
  components: Record<string, unknown>,
  playerEra: string | undefined,
): Record<string, unknown> | null {
  const tryBlock = (key: string): Record<string, unknown> | null => {
    const b = components[key]
    if (!isObj(b)) return null
    if (!isObj(b.production)) return null
    return b
  }

  if (playerEra) {
    const b = tryBlock(playerEra)
    if (b) return b
  }
  const allAge = tryBlock('AllAge')
  if (allAge) return allAge
  for (const k of Object.keys(components)) {
    if (k === 'AllAge') continue
    const b = tryBlock(k)
    if (b) return b
  }
  return null
}

/**
 * Walk `available_products[]` (the standard building format) for per-day
 * production. Each entry: `{ production_time, product?, unit_type_id?, amount? }`.
 * Standard buildings (residential / production / goods / culture) use
 * `product.resources`; military buildings use `unit_type_id` + `amount` +
 * `time_to_train`.
 *
 * v1 simplification: take only the FIRST entry as the building's "default"
 * per-day output. A real residential has ONE entry, so first = sum. A
 * multi-option goods building has 2-3 entries (different cycle times for
 * different goods) — a player picks ONE per cycle, so first is a closer
 * proxy than summing all. Same applies to multi-unit military buildings.
 *
 * Tracking the player's actively-selected option lives in `placement.state`
 * which Phase 4 doesn't surface yet. If smoke says the numbers feel off
 * (e.g. wrong goods type counted), revisit by parsing the state.
 */
function readAvailableProducts(rawEntity: Record<string, unknown>, into: CycleContribution): void {
  const products = rawEntity.available_products
  if (!Array.isArray(products) || products.length === 0) return
  const p = products[0]
  if (!isObj(p)) return
  const cycle = typeof p.production_time === 'number' ? p.production_time : 0
  if (cycle <= 0) return
  const perDayMultiplier = SECONDS_PER_DAY / cycle

  // Standard product: { product: { resources: { money: 100, ... } } }
  const product = isObj(p.product) ? p.product : null
  const resources = product && isObj(product.resources) ? product.resources : null
  if (resources) {
    for (const [key, val] of Object.entries(resources)) {
      if (typeof val === 'number') addResource(into.resources, key, val * perDayMultiplier)
    }
  }

  // Military product: { unit_type_id, amount, time_to_train }
  if (typeof p.unit_type_id === 'string' && typeof p.amount === 'number') {
    const trainSec = typeof p.time_to_train === 'number' ? p.time_to_train : cycle
    if (trainSec > 0) {
      const perDay = (SECONDS_PER_DAY / trainSec) * p.amount
      if (Number.isFinite(perDay) && perDay > 0) {
        into.units.set(p.unit_type_id, (into.units.get(p.unit_type_id) ?? 0) + perDay)
      }
    }
  }
}

/**
 * Walk `components.{Era|AllAge}.production.options[*]` for per-day output.
 * Each option: `{ time, products: [{ type, playerResources: { resources: {...} } }] }`.
 * Only `type === 'resources'` contributes — `random` and `genericReward`
 * options surface as opaque rewards (Phase 7 polish if useful).
 *
 * v1 simplification: take the FIRST option as the building's "default"
 * per-day output. A real player picks ONE option per cycle; the actively-
 * selected option lives in `placement.state` which Phase 4 doesn't surface.
 * Summing all options 3-4× over-counts the building's contribution.
 */
function readComponentsProduction(
  rawEntity: Record<string, unknown>,
  playerEra: string | undefined,
  into: CycleContribution,
): void {
  const components = rawEntity.components
  if (!isObj(components)) return
  const block = pickProductionBlock(components, playerEra)
  if (!block) return
  const production = block.production
  if (!isObj(production)) return
  const options = production.options
  if (!Array.isArray(options) || options.length === 0) return
  const opt = options[0]
  if (!isObj(opt)) return
  const cycle = typeof opt.time === 'number' ? opt.time : 0
  if (cycle <= 0) return
  const perDayMultiplier = SECONDS_PER_DAY / cycle
  if (!Array.isArray(opt.products)) return
  for (const p of opt.products) {
    if (!isObj(p)) continue
    if (p.type !== 'resources') continue
    const playerResources = isObj(p.playerResources) ? p.playerResources : null
    const resources =
      playerResources && isObj(playerResources.resources) ? playerResources.resources : null
    if (!resources) continue
    for (const [key, val] of Object.entries(resources)) {
      if (typeof val === 'number') addResource(into.resources, key, val * perDayMultiplier)
    }
  }
}

/**
 * Per-day cycle production for a single building. Reads both the standard
 * `available_products[]` path AND the components `{Era|AllAge}.production`
 * path; some entities in real exports have one or the other, and a few
 * (transitional / level-able events) have both.
 *
 * Caller responsibility:
 *   - Skip invalid buildings (disconnected / outside / overlapping).
 *   - Skip non-productive types (decoration / street / off_grid).
 *   - Pass the player's era for multi-age building resolution.
 *
 * The function never throws — silent return of zeros for malformed entities
 * is the contract (matches the validator's permissive style).
 */
export function resolveCycleProduction(
  rawEntity: unknown,
  playerEra: string | undefined,
): CycleContribution {
  const out: CycleContribution = { resources: new Map(), units: new Map() }
  if (!isObj(rawEntity)) return out
  readAvailableProducts(rawEntity, out)
  readComponentsProduction(rawEntity, playerEra, out)
  return out
}

/**
 * Capacity / static contributions for a single building. Reads:
 *
 *   - `staticResources.resources.{population, happiness, money, supplies, ...}`
 *   - `components.AllAge.staticResources.resources.*`
 *   - `provided_happiness` (top-level, culture buildings)
 *   - `components.{Era}.happiness.provided` (multi-age bonus residentials)
 *   - `entity_levels[0].provided_happiness` (v1 simplification — the active
 *     entity_level lives in `placement.state` and isn't surfaced yet)
 *
 * Returns a flat ResourceMap so callers don't have to know which path
 * provided which key. Callers SHOULD treat keys like `population` and
 * `happiness` as totals (capacities); other keys (`money`, `supplies`,
 * `strategy_points`) as per-day flows. In practice only population and
 * happiness consistently appear here; the others are documented for
 * completeness and to match the renderer's "zero is fine" contract.
 */
export function resolveStaticContribution(
  rawEntity: unknown,
  playerEra: string | undefined,
): StaticContribution {
  const out: StaticContribution = { resources: new Map() }
  if (!isObj(rawEntity)) return out

  // 1. Top-level staticResources.resources.
  const sr = rawEntity.staticResources
  if (isObj(sr) && isObj(sr.resources)) {
    for (const [key, val] of Object.entries(sr.resources)) {
      if (typeof val === 'number') addResource(out.resources, key, val)
    }
  }

  // 2. components.{AllAge,Era}.staticResources — multi-age events store
  //    capacity-style data per era under a doubly-nested resources path:
  //
  //      components.{Era}.staticResources = {
  //        type: 'staticResources',
  //        resources: { resources: { population: -1070 } }
  //      }
  //
  //    Note the *double* `.resources.resources` — different shape from
  //    top-level. Values can be NEGATIVE (a residential that consumes
  //    population at higher tiers, like the Trailside-class events).
  //    These were the Phase 4 audit miss: 247 of 247 placed multi-age
  //    "residential"-typed event buildings encode population/happiness
  //    here, not at the top level.
  const components = rawEntity.components
  if (isObj(components)) {
    const eraBlock = pickEraBlockWithStaticResources(components, playerEra)
    if (eraBlock) {
      readDoubleNestedResources(eraBlock.staticResources, out.resources)
    }
    // AllAge is independent of the era pick — combine them defensively.
    const allAge = components.AllAge
    if (isObj(allAge) && isObj(allAge.staticResources)) {
      readDoubleNestedResources(allAge.staticResources, out.resources)
      // Some AllAge entries use the simpler single-nested shape too —
      // try that fallback. If both paths are populated for one entity
      // (rare), they're additive (the addResource helper sums).
      const aRes = allAge.staticResources.resources
      if (isObj(aRes) && !isObj((aRes as Record<string, unknown>).resources)) {
        for (const [key, val] of Object.entries(aRes)) {
          if (typeof val === 'number') addResource(out.resources, key, val)
        }
      }
    }

    // 3. components.{Era}.happiness.provided — multi-age bonus residentials
    //    (Trailside Smithy etc.). Pick the player's era; fall back to first.
    const block = pickHappinessBlock(components, playerEra)
    if (block) {
      const h = block.happiness
      if (isObj(h) && typeof h.provided === 'number') {
        addResource(out.resources, 'happiness', h.provided)
      }
    }
  }

  // 4. Top-level provided_happiness — the canonical culture-building path.
  const ph = rawEntity.provided_happiness
  if (typeof ph === 'number') addResource(out.resources, 'happiness', ph)

  // 5. entity_levels[0].provided_happiness — level-able event buildings.
  //    v1 reads level 0 as a simplification; the actual placed level lives
  //    in placement.state and isn't typed yet.
  const lvls = rawEntity.entity_levels
  if (Array.isArray(lvls) && lvls.length > 0 && isObj(lvls[0])) {
    const lvlPh = lvls[0].provided_happiness
    if (typeof lvlPh === 'number') addResource(out.resources, 'happiness', lvlPh)
  }

  return out
}

/**
 * Pick the era block with a `happiness.provided` value. When `playerEra` is
 * supplied, only that era is tried — same reasoning as
 * `pickEraBlockWithStaticResources`: a building's happiness at era X
 * doesn't apply to a player in era Y.
 */
function pickHappinessBlock(
  components: Record<string, unknown>,
  playerEra: string | undefined,
): Record<string, unknown> | null {
  const tryBlock = (key: string): Record<string, unknown> | null => {
    const b = components[key]
    if (!isObj(b)) return null
    if (!isObj(b.happiness)) return null
    return b
  }
  if (playerEra) {
    return tryBlock(playerEra)
  }
  for (const k of Object.keys(components)) {
    if (k === 'AllAge') continue
    const b = tryBlock(k)
    if (b) return b
  }
  return null
}

/**
 * Map a `production_bonus.type` to the canonical resource key Phase 4's
 * stats engine accumulates. Unknown types return null — the GB won't
 * contribute to any total. This list is intentionally small in v1; types
 * like `contribution_boost` (guild treasury %) and `att_boost_attacker`
 * are multiplicative or out-of-scope for the player's direct production.
 *
 * `clan_goods` is excluded: it represents the GB's daily contribution to
 * the GUILD treasury, not the player's personal production. Including it
 * would inflate the city's "Goods/day" number by guild-bound output.
 */
function mapBonusTypeToResource(type: string): string | null {
  switch (type) {
    case 'strategy_points':
      return 'strategy_points'
    case 'medals':
      return 'medals'
    case 'happiness':
      return 'happiness'
    case 'population':
      return 'population'
    case 'goods':
      return 'goods' // generic goods (rare; usually per-age via `previous_age_goods`)
    case 'money':
      return 'money'
    case 'supplies':
      return 'supplies'
    default:
      return null
  }
}

/**
 * Read a Great Building's per-level bonus values for the placed level.
 * `production_bonus.values` is 1-indexed (`values[0] === 0` for level 0;
 * `values[L]` for a GB at level L). Levels above the tabulated tiers clamp
 * to `values.length - 1`, matching in-game cap behaviour.
 *
 * Returns an empty Map when:
 *   - The entity isn't a GB.
 *   - The GB's `production_bonus.type` isn't surfaced in v1 (see
 *     `mapBonusTypeToResource`).
 *   - `level` is undefined or non-positive.
 *
 * v1 ONLY reads `production_bonus`. `passive_bonus` is intentionally
 * excluded — passive bonuses on most GBs are guild / fight-affecting
 * (contribution_boost, att/def boosts) and don't translate into a
 * per-day production number. Specific cases that DO translate (e.g.
 * Lighthouse's supplies passive_bonus.type) can be folded into v2 if
 * the smoke pass surfaces the gap.
 */
export function resolveGBLevelBonus(
  rawEntity: unknown,
  level: number | undefined,
): GBLevelBonus {
  const out: GBLevelBonus = { resources: new Map() }
  if (!isObj(rawEntity)) return out
  if (typeof level !== 'number' || level <= 0) return out

  const pb = rawEntity.production_bonus
  if (!isObj(pb)) return out
  if (typeof pb.type !== 'string') return out
  const values = pb.values
  if (!Array.isArray(values)) return out

  const resourceKey = mapBonusTypeToResource(pb.type)
  if (!resourceKey) return out

  const idx = Math.min(Math.max(0, Math.floor(level)), values.length - 1)
  const v = values[idx]
  if (typeof v !== 'number' || v === 0) return out

  out.resources.set(resourceKey, v)
  return out
}
